/**
 * Pure animation-target function for the v2 card deck.
 *
 * Given a card's status, its position in the visible deck, the total deck
 * size, and the current view mode, returns the Motion `animate` target plus
 * the transition that should drive the change.
 *
 * Everything that was hard-coded inside `SmbActionList` (stack offsets,
 * skip-peel translate/rotate, exit fades) lives here in one place. The list
 * renderer is a pure consumer.
 */
import { motionTransitions } from "@/web/lib/motion-tokens";

export type ViewMode = "stack" | "grid";

export type CardStatus =
  | { kind: "idle" }
  | { kind: "swipe-pending"; direction: "left" | "right" }
  | { kind: "exit-skip" }
  | { kind: "exit-complete" };

const STACK_STEP = -20;
const NUM_STACKED_CARDS = 4;
const SKIP_OUT_TRANSLATE_X = "-118%";

export type DeckCardTarget = {
  opacity: number;
  scale: number;
  zIndex: number;
  rotateZ: number;
  rotateY: number;
  x: string | number;
  y: number;
};

function stackBase(index: number, total: number): DeckCardTarget {
  const clamped = Math.min(index, NUM_STACKED_CARDS - 1);
  return {
    opacity: 1,
    scale: 1 - clamped / 30,
    zIndex: total - index,
    rotateZ: 0,
    rotateY: 0,
    x: 0,
    y: clamped * STACK_STEP,
  };
}

function gridBase(index: number, total: number): DeckCardTarget {
  return {
    opacity: 1,
    scale: 1,
    zIndex: total - index,
    rotateZ: 0,
    rotateY: 0,
    x: 0,
    y: 0,
  };
}

export function deckAnimate({
  status,
  index,
  total,
  viewMode,
}: {
  status: CardStatus;
  index: number;
  total: number;
  viewMode: ViewMode;
}): DeckCardTarget {
  const base = viewMode === "stack" ? stackBase(index, total) : gridBase(index, total);

  switch (status.kind) {
    case "idle":
    case "swipe-pending":
      // swipe-pending leaves the deck wrapper in its idle pose. The visible
      // "card sits at apex" motion is owned by the inner `Swipable` translate.
      // The deck only needs to not fight it (no x/y overrides here).
      return base;

    case "exit-skip":
      if (viewMode === "stack") {
        return {
          ...base,
          scale: base.scale * 0.92,
          zIndex: total + 2,
          rotateZ: -11,
          rotateY: 10,
          x: SKIP_OUT_TRANSLATE_X,
          y: base.y - 10,
          // Ends at opacity 0 so the snap-back-to-back-of-stack on reinsertion
          // is invisible; the fade-in from idle's opacity:1 covers reappearance.
          opacity: 0,
        };
      }
      return { ...base, opacity: 0, scale: 0.96 };

    case "exit-complete":
      if (viewMode === "stack") {
        // Fade in place; siblings' indices already decremented, so their
        // springs carry the "stack moves up" motion independently.
        return { ...base, opacity: 0, scale: base.scale * 0.96 };
      }
      return { ...base, opacity: 0, scale: 0.96 };
  }
}

export function deckTransition({ status, viewMode }: { status: CardStatus; viewMode: ViewMode }) {
  if (status.kind === "exit-skip" && viewMode === "stack") {
    return motionTransitions.skipOut;
  }
  if (status.kind === "exit-skip" || status.kind === "exit-complete") {
    return motionTransitions.cardFade;
  }
  return motionTransitions.cardLayoutSpring;
}

/**
 * Sizing for a card in stack mode. Shared so anything that needs to sit
 * flush against the card — currently the "3 of 7" position cue — can occupy
 * an identically-sized box instead of re-deriving the height.
 *
 * Re-deriving is not viable: the effective height is the *minimum* of a
 * viewport-relative calc, `max-h-128`, and whatever `aspect-7/10` implies once
 * `sm:max-w-88` clamps the width. A hand-written `calc()` would silently drift
 * from the card the first time any of those changes.
 */
export const STACK_CARD_SIZE_CLASS =
  "aspect-7/10 h-[min(calc(100cqh-4rem),calc(100cqw*10/7))] max-h-128 w-auto max-w-full sm:max-w-88";

export const deckAnimateConstants = {
  STACK_STEP,
  NUM_STACKED_CARDS,
  SKIP_OUT_TRANSLATE_X,
} as const;
